import { inject } from "@angular/core";
import { ActivatedRouteSnapshot, ResolveFn, Router } from "@angular/router";
import { CookieService } from "ngx-cookie-service";
import { catchError, map, Observable, of, switchMap, throwError } from "rxjs";
import { ConfigService } from "src/app/servicios/config/config.service";
import { RESPONSE } from "src/express.tokens";
import { DetalleNoticiasService } from "../../servicios/detalle-noticias/detalle-noticias.service";
import { PlatformService } from "../../servicios/platform/platform.service";

/**
 * Resolver para la página de detalle de noticia.
 * Solo obtiene la noticia principal (getVideo) para minimizar el TTFB.
 * Los datos secundarios (comentarios, relacionadas, nextPrev, últimas sección)
 * se cargan de forma diferida en el componente del lado del navegador.
 *
 * @param {ActivatedRouteSnapshot} route
 * @returns {Observable<any>} - Un observable con la noticia principal.
 */
export const VideoResolver: ResolveFn<any> = (route: ActivatedRouteSnapshot): Observable<any> => {
  const _platformService = inject(PlatformService);
  const _detalleNoticiasService = inject(DetalleNoticiasService);
  const _configService = inject(ConfigService);
  const _cookieService = inject(CookieService);
  const _constantesGlobales = _configService.getConstantesGlobales();

  let _router: Router | null = null;
  let _response: any = null;

  if (_platformService.isBrowser()) {
    _router = inject(Router);
  } else {
    _response = inject(RESPONSE, { optional: true });
  }

  const idParam = route.paramMap.get("id");
  const search = isNaN(+idParam) ? idParam : +idParam;
  const by = isNaN(+idParam) ? "slug" : "id";
  const urlFrom = route.paramMap.get("id3") === "isquadPrensa" ? route.paramMap.get("id3") : "";

  let userLogin = false;
  if (_platformService.isBrowser()) {
    userLogin = _cookieService.get("userLogin") ? true : false;
  }

  // Solo pedimos la noticia principal. El resto se carga en el componente (browser-side).
  return _detalleNoticiasService.getVideoPrensa(_constantesGlobales.idAmbito, search, "noticia", by, userLogin, urlFrom).pipe(
    map((result) => result ?? null),
    // Una vez tenemos el video, extraemos el subcanal para pasarlo al componente
    switchMap((videoData) => {
      let subcanal = null;
      if (videoData) {
        const noticia = Array.isArray(videoData) ? videoData[0] : videoData;
        subcanal = noticia?.idSubcanal || null;
      }
      return of({
        video: videoData,
        subcanal: subcanal,
        // Inicializamos a null. El componente los cargará en el browser.
        ultimasNoticiasSeccion: null,
        relacionadas: null,
        nextPrev: null,
        comentarios: null,
      });
    }),
    catchError(() => {
      // Control de redirección a 404
      if (_platformService.isBrowser()) {
        _router?.navigateByUrl("/404");
      } else if (_response) {
        _response.redirect(301, "/404");
      }
      return throwError(() => new Error("Noticia no encontrada"));
    }),
  );
};
